import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  UserPlus,
  ArrowLeft,
  Mail,
  User,
  Lock,
  Building,
  Shield,
  AlertCircle,
  Check,
} from "lucide-react";
import Header from "../../components/Header";
import {
  getAuthenticatedAxios,
  buildApiPath,
  API_ENDPOINTS,
} from "../../utils/api";

function CreateUser() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    email: "",
    full_name: "",
    password: "",
    confirm_password: "",
    company_id: "",
    role: "admin",
    is_active: true,
    search_limit: 100,
  });

  const [errors, setErrors] = useState({});

  const loadCompanies = useCallback(async () => {
    setLoadingCompanies(true);
    try {
      const axios = getAuthenticatedAxios();
      const response = await axios.get(
        buildApiPath(API_ENDPOINTS.ADMIN.COMPANIES),
      );
      const companiesData = response.data.items || [];

      // Only use companies with valid IDs - no synthetic IDs
      const validCompanies = companiesData
        .filter((company) => {
          const id = company.id || company.company_id || company.CompanyId;
          return id && typeof id === "string" && id.length > 0;
        })
        .map((company) => ({
          company_id: company.id || company.company_id || company.CompanyId,
          name: company.name || company.Name || "Unnamed Company",
        }));

      if (validCompanies.length === 0) {
        setError("No companies available. Please create a company first.");
      } else {
        setCompanies(validCompanies);
        // Set first company as default if available
        setFormData((prev) =>
          prev.company_id
            ? prev
            : {
                ...prev,
                company_id: validCompanies[0].company_id,
              },
        );
      }
    } catch (err) {
      console.error("Failed to load companies:", err);
      setError("Failed to load companies. Please try again.");
    } finally {
      setLoadingCompanies(false);
    }
  }, []);

  // Load companies for selection
  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Email validation - lowercase for consistency
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    // Name validation
    if (!formData.full_name) {
      newErrors.full_name = "Full name is required";
    }

    // Password validation - aligned with backend
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else {
      const hasLength = formData.password.length >= 8;
      const hasUppercase = /[A-Z]/.test(formData.password);
      const hasLowercase = /[a-z]/.test(formData.password);
      const hasDigit = /\d/.test(formData.password);
      const hasSpecial = /[^A-Za-z0-9]/.test(formData.password);

      const checks = [
        hasLength,
        hasUppercase,
        hasLowercase,
        hasDigit,
        hasSpecial,
      ];
      const passedChecks = checks.filter(Boolean).length;

      if (!hasLength) {
        newErrors.password = "Password must be at least 8 characters";
      } else if (passedChecks < 3) {
        const missing = [];
        if (!hasUppercase) missing.push("uppercase letter");
        if (!hasLowercase) missing.push("lowercase letter");
        if (!hasDigit) missing.push("number");
        if (!hasSpecial)
          missing.push("special character (!@#$%^&*()_+-=[]{}|;:,.<>?)");
        newErrors.password = `Password needs at least 3 of these: ${missing.join(", ")}`;
      }
    }

    // Confirm password validation
    if (formData.password !== formData.confirm_password) {
      newErrors.confirm_password = "Passwords do not match";
    }

    // Company validation
    if (!formData.company_id) {
      newErrors.company_id = "Company is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const axios = getAuthenticatedAxios();

      // Prepare request data - lowercase email for consistency
      const requestData = {
        email: formData.email.toLowerCase().trim(),
        full_name: formData.full_name.trim(),
        password: formData.password,
        company_id: formData.company_id,
        role: formData.role,
        is_active: formData.is_active,
        search_limit: formData.search_limit,
      };

      await axios.post(buildApiPath(API_ENDPOINTS.ADMIN.CREATE_USER), requestData);

      setSuccess(true);

      // Redirect after success
      setTimeout(() => {
        navigate("/admin/users");
      }, 2000);
    } catch (err) {
      console.error("Failed to create user:", err);

      // Handle different error types with specific messages
      if (err.response?.status === 409) {
        setError("A user with this email already exists");
      } else if (err.response?.status === 400) {
        // Validation errors from backend
        const detail = err.response.data?.detail;
        if (typeof detail === "string") {
          setError(detail);
        } else if (detail?.msg) {
          setError(detail.msg);
        } else {
          setError("Invalid data provided. Please check your inputs.");
        }
      } else if (err.response?.status === 422) {
        // Pydantic validation errors
        const errors = err.response.data?.detail;
        if (Array.isArray(errors) && errors.length > 0) {
          const errorMessages = errors
            .map((e) => e.msg || e.message)
            .join(", ");
          setError(errorMessages);
        } else {
          setError("Validation failed. Please check your inputs.");
        }
      } else if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else if (err.message) {
        setError(err.message);
      } else {
        setError("Failed to create user. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === "checkbox" ? checked : value;

    // Auto-set search limit when role changes to demo
    if (name === "role") {
      setFormData((prev) => ({
        ...prev,
        [name]: newValue,
        search_limit: newValue === "demo" ? 10 : 100,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: newValue,
      }));
    }

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center">
            <button
              onClick={() => navigate("/admin/users")}
              className="mr-4 p-2 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <UserPlus className="h-8 w-8 mr-3 text-blue-600" />
                Create Admin or Demo User
              </h1>
              <p className="mt-1 text-gray-600">
                Create an admin or demo user with a password you provide
              </p>
            </div>
          </div>
        </div>

        {/* Success Message */}
        {success && (
          <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg flex items-center">
            <Check className="h-5 w-5 mr-2" />
            User created successfully! Redirecting...
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg flex items-center">
            <AlertCircle className="h-5 w-5 mr-2" />
            {error}
          </div>
        )}

        {/* Form */}
        <div className="bg-white shadow rounded-lg p-6">
          {/* Info Box */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-semibold mb-1">Admin/Demo User Creation</p>
                <p>
                  You are creating an admin or demo user with a password you set
                  directly. Please securely share this password with the user
                  after creation.
                </p>
                <p className="mt-2">
                  For regular users, use the invite flow (coming soon) which
                  will send them a secure link to set their own password.
                </p>
              </div>
            </div>
          </div>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Email Address *
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.email ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="user@example.com"
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            {/* Full Name */}
            <div>
              <label
                htmlFor="full_name"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Full Name *
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  id="full_name"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.full_name ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="John Doe"
                />
              </div>
              {errors.full_name && (
                <p className="mt-1 text-sm text-red-600">{errors.full_name}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Password *
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.password ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="••••••••"
                />
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Must be at least 8 characters with 3 of: uppercase, lowercase,
                number, special character. You'll need to share this password
                with the user.
              </p>
            </div>

            {/* Confirm Password */}
            <div>
              <label
                htmlFor="confirm_password"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Confirm Password *
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="password"
                  id="confirm_password"
                  name="confirm_password"
                  value={formData.confirm_password}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.confirm_password
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  placeholder="••••••••"
                />
              </div>
              {errors.confirm_password && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.confirm_password}
                </p>
              )}
            </div>

            {/* Company */}
            <div>
              <label
                htmlFor="company_id"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Company *
              </label>
              <div className="relative">
                <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <select
                  id="company_id"
                  name="company_id"
                  value={formData.company_id}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.company_id ? "border-red-500" : "border-gray-300"
                  }`}
                >
                  <option value="">Select a company</option>
                  {companies.map((company) => (
                    <option key={company.company_id} value={company.company_id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>
              {errors.company_id && (
                <p className="mt-1 text-sm text-red-600">{errors.company_id}</p>
              )}
            </div>

            {/* Role */}
            <div>
              <label
                htmlFor="role"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Role
              </label>
              <div className="relative">
                <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <select
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="admin">Admin</option>
                  <option value="user">Regular User</option>
                  <option value="demo">Demo User</option>
                </select>
              </div>
              {formData.role === "demo" && (
                <p className="mt-1 text-xs text-amber-600">
                  Demo accounts typically start with 10 searches per month
                </p>
              )}
            </div>

            {/* Search Limit */}
            <div>
              <label
                htmlFor="search_limit"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Monthly Search Limit
              </label>
              <input
                type="number"
                id="search_limit"
                name="search_limit"
                value={formData.search_limit}
                onChange={handleChange}
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="mt-1 text-xs text-gray-500">
                Number of searches allowed per month (resets on the 1st)
              </p>
            </div>

            {/* Active Status */}
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Active</span>
              </label>
              <p className="mt-1 text-xs text-gray-500">
                Inactive users cannot log in to the system
              </p>
            </div>

            {/* Submit Buttons */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => navigate("/admin/users")}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || loadingCompanies}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <UserPlus className="h-4 w-4" />
                    Create User
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default CreateUser;
